import {
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useGT } from 'gt-react';
import { XIcon } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// The separators the procurement criteria grid understands — commas and
// newlines. Kept as the default so its behavior is unchanged by `splitPattern`.
const DEFAULT_SPLIT_PATTERN = /[,\n]/;

interface MultiValueInputProps {
  /**
   * The stored value, comma-joined (`'a, b, c'`). This is exactly the shape the
   * criteria grid persists and `buildFilterValue` splits on, so the IR
   * round-trips unchanged.
   */
  value: string;
  /** Called with the next comma-joined string whenever pills change. */
  onChange: (next: string) => void;
  placeholder?: string;
  className?: string;
  /** The text input's id, so a visible `<label htmlFor>` can name it. */
  inputId?: string;
  /**
   * What pasted (and committed) text is split on. Defaults to commas/newlines —
   * the criteria grid's separators. The item catalog passes a whitespace-
   * inclusive pattern so a column pasted from Excel (tab- or newline-separated)
   * lands as one pill per value. The comma-joined `value` round-trip requires
   * that no committed pill contain a comma, which any pattern matching `,`
   * preserves.
   */
  splitPattern?: RegExp;
  /**
   * Replaces the default pill row — for callers that collapse large value sets
   * into a summary chip (the item catalog above 50 values). Receives the pills
   * and the same `removePill` the default rendering uses; the input itself
   * (typing, paste, Backspace-removes-last) is unaffected.
   */
  renderValues?: (
    pills: string[],
    removePill: (index: number) => void,
  ) => ReactNode;
}

/** Split a comma-joined stored value into individual, trimmed, non-empty pills. */
function splitValue(value: string): string[] {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

/** Join pills back into the comma-joined stored representation. */
function joinValue(pills: string[]): string {
  return pills.join(', ');
}

/** Append entries, stripping blanks and duplicates (case-sensitive). A Set
 * carries the membership test so a paste of thousands of values stays linear. */
function addEntries(existing: string[], entries: string[]): string[] {
  const next = [...existing];
  const seen = new Set(existing);
  for (const entry of entries) {
    const trimmed = entry.trim();
    if (trimmed !== '' && !seen.has(trimmed)) {
      seen.add(trimmed);
      next.push(trimmed);
    }
  }
  return next;
}

/**
 * A pill/tag input for multi-value inputs (the query builder's `in` / `nin`
 * operators, the item catalog's multiple-items filter). Users commit a value
 * with Enter or comma (or paste separator-delimited text); pills are
 * individually removable, and Backspace on an empty input removes the last one.
 *
 * The component speaks the comma-joined `string` interface so the IR and
 * `buildFilterValue` are untouched — it splits on input and joins on output.
 */
export function MultiValueInput({
  value,
  onChange,
  placeholder,
  className,
  splitPattern = DEFAULT_SPLIT_PATTERN,
  inputId,
  renderValues,
}: MultiValueInputProps) {
  const gt = useGT();
  const pills = splitValue(value);
  const [draft, setDraft] = useState('');

  const commitDraft = () => {
    // Split like a paste, so a draft holding several separator-delimited values
    // (only possible with a non-default pattern — comma itself commits, and a
    // single-line input can't hold a newline) commits one pill per value.
    const entries = draft.split(splitPattern);
    if (entries.every((entry) => entry.trim() === '')) return;
    onChange(joinValue(addEntries(pills, entries)));
    setDraft('');
  };

  const removePill = (index: number) => {
    onChange(joinValue(pills.filter((_, i) => i !== index)));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commitDraft();
    } else if (event.key === 'Backspace' && draft === '' && pills.length > 0) {
      event.preventDefault();
      removePill(pills.length - 1);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text');
    if (!splitPattern.test(text)) return;
    event.preventDefault();
    const entries = text.split(splitPattern);
    onChange(joinValue(addEntries(pills, entries)));
    setDraft('');
  };

  return (
    <div
      className={cn(
        'flex min-h-8 flex-1 flex-wrap items-center gap-1 rounded-md border border-input bg-transparent px-2 py-1 text-sm focus-within:ring-1 focus-within:ring-ring',
        className,
      )}
    >
      {renderValues
        ? renderValues(pills, removePill)
        : pills.map((pill, index) => (
            <Badge
              key={`${pill}-${index}`}
              variant="secondary"
              className="gap-1 py-0 pr-1 font-normal"
            >
              {pill}
              <button
                type="button"
                onClick={() => removePill(index)}
                className="flex h-3.5 w-3.5 items-center justify-center rounded-full hover:bg-muted"
                aria-label={gt('Remove {value}', {
                  value: pill,
                  context: 'aria label to remove a multi-value criterion pill',
                })}
              >
                <XIcon className="h-3 w-3" />
              </button>
            </Badge>
          ))}
      <Input
        id={inputId}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={commitDraft}
        placeholder={pills.length === 0 ? placeholder : ''}
        className="h-6 min-w-24 flex-1 border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
        autoComplete="off"
      />
    </div>
  );
}
